"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { postContact } from "@/apis/queryFunctions/contact";
import { useToast } from "@/context/ToastProvider";
import ActionButton from "@/components/Buttons/ActionButton";
import SectionTitle from "@/components/SectionTitle";

const policyFormSchema = z.object({
  ho_ten: z.string().trim().min(1, "Vui lòng nhập họ tên"),
  dien_thoai: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập số điện thoại")
    .regex(/^[+0-9\s]{9,}$/, "SĐT không hợp lệ"),
  email: z
    .string()
    .trim()
    .email("Email không hợp lệ")
    .optional()
    .or(z.literal("")),
  b_url: z.string().optional(),
});

type PolicyFormData = z.infer<typeof policyFormSchema>;

interface PolicyCardProps {
  value: string;
  label: string;
  desc: string;
  icon: React.ReactNode | string;
}

function PolicyCard({ value, label, desc, icon }: PolicyCardProps) {
  return (
    <div className="flex flex-col items-center text-center text-white p-2 md:p-4">
      {/* Icon */}
      <div className="mb-2 md:mb-3 transform hover:scale-110 transition duration-300">
        {typeof icon === "string" ? (
          <img src={icon} alt={label} className="w-8 h-8 object-contain" />
        ) : (
          icon
        )}
      </div>

      {/* Label */}
      <h4 className="text-[11px] md:text-sm font-semibold text-white/90 mb-0.5 md:mb-1 uppercase tracking-wider">
        {label}
      </h4>

      {/* Value */}
      <div className="text-2xl md:text-4xl lg:text-[42px] font-black text-white mb-1 md:mb-2 tracking-tight">
        {value}
      </div>

      {/* Desc */}
      <p className="text-[10px] md:text-xs text-white/80 leading-relaxed max-w-[180px]">
        {desc}
      </p>
    </div>
  );
}

interface PolicyItem {
  value: string;
  label: string;
  desc: string;
  icon?: string | React.ReactNode;
}

interface SalesPolicyProps {
  onDownloadSuccess: () => void;
  policies?: PolicyItem[];
  title?: string;
  subTitle?: string;
}

/* =========================================================================
   SECTION 06: CHÍNH SÁCH BÁN HÀNG
   - Mapped to ScrollHeader menu item '06: Chính sách'
   - Triggers anchor element #chinh-sach-ban-hang
   ========================================================================= */
export default function SalesPolicy({
  onDownloadSuccess,
  policies: propPolicies,
  title,
  subTitle,
}: SalesPolicyProps) {
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PolicyFormData>({
    resolver: zodResolver(policyFormSchema),
    defaultValues: {
      ho_ten: "",
      dien_thoai: "",
      email: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      if (rs && Object.keys(rs).length > 0) {
        showToast(
          "Cảm ơn Quý khách. Tài liệu CSBH đã được gửi thành công!",
          "success",
        );
        reset();
        onDownloadSuccess();
      } else {
        showToast("Gửi thông tin thất bại. Vui lòng thử lại sau.", "error");
      }
    },
    onError: () => {
      showToast("Có lỗi xảy ra, vui lòng thử lại sau.", "error");
    },
  });

  const onSubmit = (data: PolicyFormData) => {
    if (data.b_url) {
      // Honeypot triggered
      showToast(
        "Cảm ơn Quý khách. Tài liệu CSBH đã được gửi thành công!",
        "success",
      );
      reset({
        ho_ten: "",
        dien_thoai: "",
        email: "",
      });
      onDownloadSuccess();
      return;
    }
    const payload = {
      ho_ten: data.ho_ten.trim(),
      dien_thoai: data.dien_thoai.trim(),
      email: data.email?.trim() || "",
      dia_chi: "",
      noi_dung: "Đăng ký tư vấn Chính sách bán hàng Dragon Home",
      loai_hinh: "Đất nền",
    };
    contactMutation.mutate(payload);
  };

  const finalPolicies =
    propPolicies && propPolicies.length > 0 ? propPolicies : [];

  const inputClass =
    "w-full bg-white text-black px-4 py-2.5 rounded border border-gray-300 outline-none text-xs focus:ring-1 focus:ring-primary";

  return (
    <section
      id="chinh-sach-ban-hang"
      className="py-12 md:py-16 bg-gradient-to-br from-primary to-primaryDark "
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Title */}
        <SectionTitle
          title={title || "Chính sách bán hàng"}
          subtitle={subTitle}
          theme="white"
          underlineColor="white"
          className="mb-8 md:mb-16"
        />

        {/* Policies Grid */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-6 md:gap-6 lg:gap-8 mb-8 md:mb-16">
          {finalPolicies.map((p) => (
            <div
              key={p.label}
              className="w-[calc(50%-8px)] md:w-[calc(33.333%-16px)] lg:w-[calc(20%-26px)]"
            >
              <PolicyCard
                value={p.value}
                label={p.label}
                desc={p.desc}
                icon={p.icon || ""}
              />
            </div>
          ))}
        </div>

        {/* Inline Lead Capture Form */}
        <div className="max-w-4xl mx-auto bg-white p-4 md:p-8 rounded-xl shadow-2xl">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end"
          >
            {/* Honeypot field for bot spam prevention */}
            <div className="hidden" aria-hidden="true">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                placeholder="Do not fill this field"
                {...register("b_url")}
              />
            </div>
            {/* HỌ TÊN */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                Họ và tên{" "}
                <span className="text-[9px] font-light italic text-red-500 lowercase normal-case tracking-normal">
                  (*)
                </span>
              </label>
              <input
                type="text"
                placeholder="Vui lòng nhập họ và tên"
                {...register("ho_ten")}
                className={inputClass}
              />
              {errors.ho_ten && (
                <p className="text-[10px] text-red-500 font-semibold absolute mt-0.5">
                  {errors.ho_ten.message}
                </p>
              )}
            </div>

            {/* SỐ ĐIỆN THOẠI */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                Số điện thoại{" "}
                <span className="text-[9px] font-light italic text-red-500 lowercase normal-case tracking-normal">
                  (*)
                </span>
              </label>
              <input
                type="text"
                placeholder="Vui lòng nhập số điện thoại"
                {...register("dien_thoai")}
                className={inputClass}
              />
              {errors.dien_thoai && (
                <p className="text-[10px] text-red-500 font-semibold absolute mt-0.5">
                  {errors.dien_thoai.message}
                </p>
              )}
            </div>

            {/* EMAIL */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                Email{" "}
                <span className="text-[9px] font-light italic lowercase normal-case tracking-normal">
                  (Không bắt buộc)
                </span>
              </label>
              <input
                type="email"
                placeholder="Vui lòng nhập email"
                {...register("email")}
                className={inputClass}
              />
              {errors.email && (
                <p className="text-[10px] text-red-500 font-semibold absolute mt-0.5">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* SUBMIT BUTTON */}
            <div>
              <ActionButton
                type="submit"
                label={
                  contactMutation.isPending ? "Đang gửi..." : "Liên hệ ngay"
                }
                className="w-full"
              />
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
